/**
 * Visitor Layer - Fase 1 Core Engine
 * Identificar navegador único persistente.
 * Persistência: localStorage (principal) + cookie (fallback)
 */

import type { Visitor } from '../types';
import { APP_NAME, VISITOR_TTL_MS } from '../config';
import { getFingerprint } from '../utils/getFingerprint';

const VISITOR_STORAGE_KEY = `${APP_NAME}_VISITOR_V1`;
const VISITOR_COOKIE_KEY = `${APP_NAME.toLowerCase()}_visitor_id`;

function generateVisitorId(): string {
  const ts = Date.now();
  const r = Math.random().toString(36).substring(2, 15);
  return `v_${ts}_${r}`;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const eq = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(eq) === 0) return c.substring(eq.length);
  }
  return null;
}

function setCookie(name: string, value: string, maxAgeMs: number): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + maxAgeMs).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function loadFromStorage(): Visitor | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(VISITOR_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Visitor;
    if (!v?.visitorId || !v?.lastSeenAt) return null;
    if (Date.now() - v.lastSeenAt > VISITOR_TTL_MS) return null;
    return v;
  } catch {
    return null;
  }
}

function saveToStorage(visitor: Visitor): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify(visitor));
    }
    setCookie(VISITOR_COOKIE_KEY, visitor.visitorId, VISITOR_TTL_MS);
  } catch {
    // persistência opcional
  }
}

export interface GetOrCreateVisitorOptions {
  /** Incluir fingerprint no visitor (opcional). */
  withFingerprint?: boolean;
  /** Log de debug. */
  debug?: boolean;
}

/**
 * Obtém ou cria o visitor persistente.
 * TTL 1 ano; firstSeenAt/lastSeenAt atualizados; fingerprint opcional.
 */
export async function getOrCreateVisitor(
  options: GetOrCreateVisitorOptions = {}
): Promise<Visitor> {
  const { withFingerprint = false, debug = false } = options;
  const now = Date.now();

  let visitor = loadFromStorage();
  const fromCookie = getCookie(VISITOR_COOKIE_KEY);

  if (visitor) {
    visitor.lastSeenAt = now;
    saveToStorage(visitor);
    if (debug) {
      console.log('[AgTracker Visitor] Recuperado:', visitor.visitorId);
    }
    return visitor;
  }

  // Fallback: cookie com mesmo ID para não criar novo visitor
  const visitorId = fromCookie
    ? decodeURIComponent(fromCookie)
    : generateVisitorId();
  const isNew = !fromCookie;

  const fingerprint = withFingerprint ? await getFingerprint() : undefined;

  visitor = {
    visitorId,
    firstSeenAt: isNew ? now : now,
    lastSeenAt: now,
    fingerprint,
  };

  saveToStorage(visitor);

  if (debug) {
    console.log('[AgTracker Visitor] Criado:', visitor.visitorId, { isNew });
  }

  return visitor;
}

/**
 * Versão síncrona que usa apenas storage/cookie (sem fingerprint).
 * Útil para obter visitorId imediatamente antes de getOrCreateVisitor().
 */
export function getOrCreateVisitorSync(): Visitor | null {
  const now = Date.now();
  const stored = loadFromStorage();
  if (stored) {
    stored.lastSeenAt = now;
    saveToStorage(stored);
    return stored;
  }
  const fromCookie = getCookie(VISITOR_COOKIE_KEY);
  const visitorId = fromCookie
    ? decodeURIComponent(fromCookie)
    : generateVisitorId();
  const visitor: Visitor = {
    visitorId,
    firstSeenAt: now,
    lastSeenAt: now,
  };
  saveToStorage(visitor);
  return visitor;
}
